import { Books, ChartScatter, Flask, Gear, ListBullets, Plus } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useCollections,
  useCurrentWorkspace,
  useGitConnections,
  useProjects,
  useStars,
} from "~/api/queries";
import { ABILITY, type Abilities } from "~/api/types";
import { KNOWLEDGE_SECTIONS, knowledgeLocation } from "~/features/analysis/knowledgeNavigationModel";
import { RepositoryNodes } from "~/features/git/RepoTree";
import { ProjectTree } from "~/features/projects/ProjectTree";
import { Scrollable } from "~/foundation/components/Scrollable";
import { Spinner } from "~/foundation/components/Spinner";
import { NavLauncher } from "~/shell/NavLauncher";
import { NavResults, PinToggle } from "~/shell/NavPrimitives";
import { isNavItemActive, visibleNavSections } from "~/shell/nav";
import {
  activeDestination,
  flatten,
  type NavDestination,
  navLayerSections,
  resolveNavSection,
  searchNav,
} from "~/shell/navCatalog";
import { CollectionTree } from "~/shell/Sidebar/CollectionTree";
import { DocumentsNav } from "~/shell/Sidebar/DocumentsNav";
import { NavLayerHeader } from "~/shell/Sidebar/NavLayerHeader";
import { SidebarLink } from "~/shell/Sidebar/SidebarLink";
import { SECTION_ACTION_CLASS, SidebarSection } from "~/shell/Sidebar/SidebarSection";
import { StarredTree } from "~/shell/Sidebar/StarredTree";
import { useShellStore } from "~/shell/store";

export interface SidebarProps {
  abilities: Abilities;
  pathname: string;
  /** Mobile drawer closes on navigate; the desktop sidebar does nothing. */
  onNavigate?: () => void;
}

/** The trees' collapse state lives beside the nav sections', under ids no nav section uses. */
const COLLECTION_TREE_SECTION = "collectionTree";
/** W10 §2 — the viewer's own bookmarks. */
const STARRED_SECTION = "starred";
const PROJECT_TREE_SECTION = "projectTree";
/** 2026-09-24 — the workspace's own repositories; a project's ride under the project. */
const REPOSITORY_TREE_SECTION = "repositoryTree";
const PINNED_SECTION = "pinned";

/**
 * The workspace layer of the sidebar (layered nav, 2026-09-09): the header carries
 * "‹ All sections" (which swaps in the launcher — pinned + sections, drill-in, search) and a
 * search field over every destination; below it the pinned favourites, the nav sections with
 * a pin star on each link, and — under Workspace — the browsable trees.
 */
export function Sidebar({ abilities, pathname, onNavigate }: SidebarProps) {
  const { t } = useTranslation();
  const { data: workspace } = useCurrentWorkspace();
  const collapsedSections = useShellStore((state) => state.collapsedSections);
  const setSectionCollapsed = useShellStore((state) => state.setSectionCollapsed);
  const navLayerOpen = useShellStore((state) => state.navLayerOpen);
  const setNavLayerOpen = useShellStore((state) => state.setNavLayerOpen);
  const pinnedIds = useShellStore((state) => state.pinnedNavIds);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (pathname === "/documents" || pathname.startsWith("/documents/")) {
      setSectionCollapsed("workspace", false);
    }
  }, [pathname, setSectionCollapsed]);

  const sections = visibleNavSections(abilities);
  const layerSections = useMemo(() => navLayerSections(abilities), [abilities]);
  const catalog = useMemo(() => flatten(layerSections), [layerSections]);
  const current = resolveNavSection(layerSections, pathname);
  const active = activeDestination(catalog, pathname);
  const hits = useMemo(() => searchNav(layerSections, query, t), [layerSections, query, t]);
  const pinned = useMemo(
    () =>
      pinnedIds
        .map((id) => catalog.find((destination) => destination.id === id))
        .filter((destination): destination is NavDestination => !!destination),
    [pinnedIds, catalog],
  );

  const workspaceName = workspace?.name ?? t("Workspace");

  if (navLayerOpen) {
    return (
      <nav aria-label={t("Main")} className="flex h-full flex-col">
        <NavLauncher
          variant="sidebar"
          autoFocus
          onNavigate={() => {
            setNavLayerOpen(false);
            onNavigate?.();
          }}
          onDismiss={() => setNavLayerOpen(false)}
          backToSection={{
            label: t("Back to {{section}}", { section: workspaceName }),
            onBack: () => setNavLayerOpen(false),
          }}
        />
      </nav>
    );
  }

  const CurrentIcon = current?.icon;

  // SLATE-LATER (§15): drag-and-drop reordering attaches to the section list below.
  return (
    <nav aria-label={t("Main")} className="flex h-full flex-col">
      <NavLayerHeader
        title={workspaceName}
        {...(current ? { subtitle: t(current.title) } : {})}
        {...(CurrentIcon ? { icon: <CurrentIcon size={16} aria-hidden /> } : {})}
        onBack={() => setNavLayerOpen(true)}
        query={query}
        onQueryChange={setQuery}
      />
      <Scrollable className="px-2 pb-2">
        {query ? (
          <NavResults
            hits={hits}
            query={query}
            {...(active ? { activeId: active.id } : {})}
            onNavigate={() => {
              setQuery("");
              onNavigate?.();
            }}
          />
        ) : (
          <div className="flex flex-col gap-3">
            {pinned.length > 0 ? (
              <SidebarSection
                title={t("Pinned")}
                collapsed={collapsedSections[PINNED_SECTION] === true}
                onToggle={() =>
                  setSectionCollapsed(PINNED_SECTION, collapsedSections[PINNED_SECTION] !== true)
                }
              >
                {pinned.map((destination) => (
                  <SidebarLink
                    key={destination.id}
                    to={destination.to}
                    icon={destination.icon}
                    label={t(destination.title)}
                    active={destination.id === active?.id}
                    action={<PinToggle id={destination.id} />}
                    {...(onNavigate ? { onNavigate } : {})}
                  />
                ))}
              </SidebarSection>
            ) : null}

            {sections.map((section) => {
              const items = section.items.filter((item) => item.inSidebar !== false);
              // A folded-by-default section still opens itself for the page you are on.
              const holdsActive = items.some((item) => isNavItemActive(item, pathname));
              const collapsed =
                collapsedSections[section.id] ??
                (section.defaultCollapsed === true && !holdsActive);
              return (
                <Fragment key={section.id}>
                  <SidebarSection
                    {...(section.title ? { title: t(section.title) } : {})}
                    collapsed={collapsed}
                    onToggle={() => setSectionCollapsed(section.id, !collapsed)}
                  >
                    {section.id === "knowledge" ? KNOWLEDGE_SECTIONS.map((group, index) => (
                      <SidebarLink key={group.id} to={group.items[0].to}
                        icon={[Books, ChartScatter, Flask, Gear][index]!} label={t(group.label)}
                        active={knowledgeLocation(pathname)?.section.id === group.id}
                        {...(onNavigate ? { onNavigate } : {})} />
                    )) : items
                      .filter((item) => !item.secondary)
                      .map((item) =>
                        item.id === "documents" ? (
                          <DocumentsNav key={item.id} pathname={pathname} onNavigate={onNavigate} />
                        ) : (
                          <SidebarLink
                            key={item.id}
                            to={item.to}
                            icon={item.icon}
                            label={t(item.title)}
                            active={isNavItemActive(item, pathname)}
                            {...(item.disabled
                              ? { disabled: true }
                              : { action: <PinToggle id={item.id} /> })}
                            {...(onNavigate ? { onNavigate } : {})}
                          />
                        ),
                      )}
                    {section.id !== "knowledge" && items.some((item) => item.secondary) && (
                      <details
                        className="mt-1"
                        open={items.some(
                          (item) => item.secondary && isNavItemActive(item, pathname),
                        )}
                      >
                        <summary className="cursor-pointer rounded-sm px-2 py-2 text-body text-text-secondary hover:bg-hover-bg">
                          {t("More knowledge views")}
                        </summary>
                        {(section.id === "knowledge"
                          ? (layerSections.find((layer) => layer.id === "knowledge")?.groups ?? [])
                          : [{ id: section.id, title: undefined, items }]
                        )
                          .map((group) => ({
                            ...group,
                            items: group.items.filter((item) =>
                              items.some((entry) => entry.id === item.id && entry.secondary),
                            ),
                          }))
                          .filter((group) => group.items.length > 0)
                          .map((group) => (
                            <details
                              key={group.id}
                              open={group.items.some((item) => item.id === active?.id)}
                            >
                              <summary className="cursor-pointer px-3 py-2 font-medium text-xs text-text-secondary">
                                {t(group.title ?? "More views")}
                              </summary>
                              {group.items.map((item) => (
                                <SidebarLink
                                  key={item.id}
                                  to={item.to}
                                  icon={item.icon}
                                  label={t(item.title)}
                                  active={item.id === active?.id}
                                  action={<PinToggle id={item.id} />}
                                  {...(onNavigate ? { onNavigate } : {})}
                                />
                              ))}
                            </details>
                          ))}
                      </details>
                    )}
                  </SidebarSection>

                  {/* The browsable trees ride below the flat workspace links they elaborate on. */}
                  {section.id === "workspace" ? (
                    <WorkspaceTrees
                      abilities={abilities}
                      pathname={pathname}
                      collapsedSections={collapsedSections}
                      setSectionCollapsed={setSectionCollapsed}
                      onNavigate={onNavigate}
                    />
                  ) : null}
                </Fragment>
              );
            })}
          </div>
        )}
      </Scrollable>
    </nav>
  );
}

/**
 * Starred, Projects, Collections and the workspace's own repositories (refined 2026-10-11).
 *
 * Each thing is listed ONCE: a collection that belongs to a project lives under that project,
 * and the Collections section holds only the rest. A section with nothing to show is absent
 * rather than a heading over a "none yet" line — except when the whole workspace is empty,
 * where the empty state is the way in. Projects, collections and repositories are each one
 * request for the whole tree.
 */
function WorkspaceTrees({
  abilities,
  pathname,
  collapsedSections,
  setSectionCollapsed,
  onNavigate,
}: {
  abilities: Abilities;
  pathname: string;
  collapsedSections: Record<string, boolean>;
  setSectionCollapsed: (sectionId: string, collapsed: boolean) => void;
  onNavigate?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const { data: stars } = useStars();
  const { data: projects, isLoading: projectsLoading } = useProjects();
  const { data: collections, isLoading: collectionsLoading } = useCollections();
  const { data: git } = useGitConnections();

  // Archived and cancelled projects leave the sidebar; their collections fall back below.
  const projectList = (projects ?? []).filter(
    (project) => project.status !== "archived" && project.status !== "cancelled",
  );
  const collectionList = collections ?? [];
  const connections = git?.connections ?? [];
  const projectIds = new Set(projectList.map((project) => project.id));
  // A collection whose project is not listed (archived, no access) falls back here, never lost.
  const looseCollections = collectionList.filter(
    (collection) => !collection.projectId || !projectIds.has(collection.projectId),
  );
  const workspaceRepos = connections.filter((connection) => connection.projectId === null);
  const canCreateCollection = abilities[ABILITY.workspaceCreateCollection] === true;
  const openDialog = useShellStore((state) => state.openDialog);

  const section = (id: string) => ({
    collapsed: collapsedSections[id] === true,
    onToggle: () => setSectionCollapsed(id, collapsedSections[id] !== true),
  });

  return (
    <>
      {stars?.stars.length ? (
        <SidebarSection title={t("Starred")} {...section(STARRED_SECTION)}>
          <StarredTree pathname={pathname} onNavigate={onNavigate} />
        </SidebarSection>
      ) : null}

      <SidebarSection
        title={t("Projects")}
        {...section(PROJECT_TREE_SECTION)}
        {...(projectList.length
          ? {
              action: (
                <button
                  type="button"
                  onClick={() => openDialog({ kind: "newProject" })}
                  aria-label={t("New project")}
                  title={t("New project")}
                  className={SECTION_ACTION_CLASS}
                >
                  <Plus size={12} aria-hidden />
                </button>
              ),
            }
          : {})}
      >
        {projectsLoading ? (
          <TreeSpinner />
        ) : (
          <ProjectTree
            projects={projectList}
            collections={collectionList}
            connections={connections}
            pathname={pathname}
            onNavigate={onNavigate}
          />
        )}
      </SidebarSection>

      {collectionsLoading || looseCollections.length || !collectionList.length ? (
        <SidebarSection
          title={t("Collections")}
          {...section(COLLECTION_TREE_SECTION)}
          action={
            <>
              <Link
                to="/collections"
                onClick={onNavigate}
                aria-label={t("All collections")}
                title={t("All collections")}
                className={SECTION_ACTION_CLASS}
              >
                <ListBullets size={12} aria-hidden />
              </Link>
              {canCreateCollection ? (
                <button
                  type="button"
                  onClick={() => openDialog({ kind: "newCollection" })}
                  aria-label={t("New collection")}
                  title={t("New collection")}
                  className={SECTION_ACTION_CLASS}
                >
                  <Plus size={12} aria-hidden />
                </button>
              ) : null}
            </>
          }
        >
          {collectionsLoading ? (
            <TreeSpinner />
          ) : looseCollections.length ? (
            <CollectionTree
              collections={looseCollections}
              pathname={pathname}
              onNavigate={onNavigate}
            />
          ) : (
            <p className="flex h-8 items-center px-1.5 text-small text-text-tertiary">
              {t("No collections yet")}
            </p>
          )}
        </SidebarSection>
      ) : null}

      {workspaceRepos.length ? (
        <SidebarSection title={t("Repositories")} {...section(REPOSITORY_TREE_SECTION)}>
          <RepositoryNodes
            connections={workspaceRepos}
            pathname={pathname}
            onNavigate={onNavigate}
          />
        </SidebarSection>
      ) : null}
    </>
  );
}

function TreeSpinner() {
  const { t } = useTranslation();
  return (
    <div className="flex h-8 items-center px-1.5">
      <Spinner size={14} label={t("Loading")} />
    </div>
  );
}
